import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import {
  Upload, Image, Palette, Save, RotateCcw, Trash2, Loader2,
  Mail, Building2, Eye, FileText, Phone, MapPin, Check
} from 'lucide-react';
import api from '../lib/api';
import DoctorDocumentsSettings from '../components/settings/DoctorDocumentsSettings';
import { toast } from 'sonner';
import {
  PRESCRIPTION_TEMPLATES,
  DEFAULT_PRESCRIPTION_OPTIONS,
  DEFAULT_MIN_ROWS,
  generatePrescriptionHTML,
  openPrescriptionWindow,
  getPreviewSampleData,
} from '../lib/prescriptionTemplates';

/**
 * Bloques opcionales que el médico puede activar o desactivar en su receta.
 * El texto explica qué desaparece del documento impreso al apagarlos.
 */
const OPTION_LABELS = [
  { key: 'show_cie10', label: 'Códigos CIE-10', hint: 'Muestra el código junto a cada diagnóstico' },
  { key: 'show_next_appointment', label: 'Próxima cita', hint: 'Línea para anotar la fecha de control' },
  { key: 'show_contact_footer', label: 'Pie de contacto', hint: 'Teléfono y dirección del consultorio' },
  { key: 'show_exams', label: 'Exámenes e interconsultas', hint: 'Laboratorio, imágenes e interconsultas solicitadas' },
  { key: 'show_digital_stamp', label: 'Sello digital', hint: 'Leyenda "Documento generado digitalmente"' },
  { key: 'show_cut_line', label: 'Línea de corte', hint: 'Solo en el recetario horizontal' },
];

export default function DoctorSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctorData, setDoctorData] = useState(null);

  // Branding state
  const [logoRectangular, setLogoRectangular] = useState(null);
  const [logoSquare, setLogoSquare] = useState(null);
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [secondaryColor, setSecondaryColor] = useState('#1e40af');
  const [businessName, setBusinessName] = useState('');
  const [emailProfessional, setEmailProfessional] = useState('');

  // Configuración de la receta médica
  const [prescriptionTemplate, setPrescriptionTemplate] = useState('v1');
  const [prescriptionMinRows, setPrescriptionMinRows] = useState(DEFAULT_MIN_ROWS);
  const [prescriptionOptions, setPrescriptionOptions] = useState(DEFAULT_PRESCRIPTION_OPTIONS);
  const [phoneProfessional, setPhoneProfessional] = useState('');
  const [addressProfessional, setAddressProfessional] = useState('');
  const [rneNumber, setRneNumber] = useState('');

  const rectInputRef = useRef(null);
  const squareInputRef = useRef(null);

  useEffect(() => {
    fetchDoctorBranding();
  }, []);

  const fetchDoctorBranding = async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctors/my-branding');
      const data = response.data;
      setDoctorData(data);
      setLogoRectangular(data.logo_rectangular || null);
      setLogoSquare(data.logo_square || null);
      setPrimaryColor(data.primary_color || '#2563eb');
      setSecondaryColor(data.secondary_color || '#1e40af');
      setBusinessName(data.business_name || '');
      setEmailProfessional(data.email_professional || '');
      setPrescriptionTemplate(data.prescription_template || 'v1');
      setPrescriptionMinRows(data.prescription_min_rows || DEFAULT_MIN_ROWS);
      // Una opción que aún no está guardada se considera activada
      setPrescriptionOptions({ ...DEFAULT_PRESCRIPTION_OPTIONS, ...(data.prescription_options || {}) });
      setPhoneProfessional(data.phone_professional || '');
      setAddressProfessional(data.address_professional || '');
      setRneNumber(data.rne_number || '');
    } catch (error) {
      console.error('Error fetching branding:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/png') && !file.type.startsWith('image/jpeg')) {
      toast.error('Solo se permiten imágenes PNG o JPG');
      return;
    }

    if (file.size > 1.5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 1.5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (type === 'rectangular') {
        setLogoRectangular(event.target.result);
      } else {
        setLogoSquare(event.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        logo_rectangular: logoRectangular || '',
        logo_square: logoSquare || '',
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        business_name: businessName,
        email_professional: emailProfessional,
        prescription_template: prescriptionTemplate,
        prescription_min_rows: prescriptionMinRows,
        prescription_options: prescriptionOptions,
        phone_professional: phoneProfessional,
        address_professional: addressProfessional,
        rne_number: rneNumber,
      };

      await api.put('/doctors/my-branding', updateData);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleResetColors = () => {
    setPrimaryColor('#2563eb');
    setSecondaryColor('#1e40af');
  };

  /**
   * Abre la vista previa de la receta con datos de ejemplo.
   *
   * Usa la misma función que genera las recetas reales, así que lo que el
   * médico ve aquí es exactamente lo que se va a imprimir. Los cambios se
   * previsualizan sin necesidad de guardar primero.
   */
  const openPreview = () => {
    const { consultation, patient } = getPreviewSampleData();

    const branding = {
      full_name: doctorData?.full_name || user?.full_name || 'Nombre del Médico',
      specialization: doctorData?.specialization || 'Especialidad',
      license_number: doctorData?.license_number || 'CMP 00000',
      email: doctorData?.email || '',
      logo_rectangular: logoRectangular,
      logo_square: logoSquare,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      business_name: businessName,
      email_professional: emailProfessional,
      phone_professional: phoneProfessional,
      address_professional: addressProfessional,
      rne_number: rneNumber,
      prescription_template: prescriptionTemplate,
      prescription_min_rows: prescriptionMinRows,
      prescription_options: prescriptionOptions,
    };

    const opened = openPrescriptionWindow(
      generatePrescriptionHTML({ consultation, patient, branding }),
      { accentColor: primaryColor }
    );

    if (!opened) {
      toast.error('El navegador bloqueó la ventana emergente. Permite las ventanas emergentes para este sitio.');
    }
  };

  /** Activa o desactiva un bloque opcional de la receta */
  const toggleOption = (key) => {
    setPrescriptionOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Configuración
          </h1>
          <p className="text-muted-foreground mt-2">
            Personaliza tu logo, colores y datos profesionales para la receta médica
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPreview}>
            <Eye className="w-4 h-4 mr-2" />
            Vista Previa Receta
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Cambios
          </Button>
        </div>
      </div>

      {/* Formato de la Receta */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Formato de la Receta
            </h2>
            <p className="text-sm text-muted-foreground">
              Elige cómo se imprimen tus recetas y qué bloques incluyen
            </p>
          </div>
        </div>

        {/* Selector de plantilla */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PRESCRIPTION_TEMPLATES.map((tpl) => {
            const selected = prescriptionTemplate === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => setPrescriptionTemplate(tpl.id)}
                className={`text-left rounded-lg border-2 p-4 transition-colors ${
                  selected ? 'bg-muted/40' : 'border-border hover:border-muted-foreground/40'
                }`}
                style={selected ? { borderColor: primaryColor } : undefined}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="font-semibold">{tpl.name}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Versión {tpl.id.replace('v', '')}</p>
                  </div>
                  {selected && (
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Check className="w-3 h-3 text-white" />
                    </span>
                  )}
                </div>

                {/* Miniatura esquemática del formato */}
                {tpl.id === 'v1' ? (
                  <div className="flex gap-2 mb-3">
                    {[0, 1].map((i) => (
                      <div key={i} className="w-12 h-16 rounded border border-border bg-background p-1 space-y-1">
                        <div className="h-1.5 rounded" style={{ backgroundColor: primaryColor }} />
                        <div className="h-1 bg-muted rounded" />
                        <div className="h-1 bg-muted rounded w-2/3" />
                        <div className="h-1 bg-muted rounded" />
                        <div className="h-1 bg-muted rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="w-32 h-16 rounded border border-border bg-background p-1 flex gap-1">
                      <div className="flex-1 space-y-1">
                        <div className="h-1.5 rounded" style={{ backgroundColor: primaryColor }} />
                        <div className="h-1 bg-muted rounded" />
                        <div className="h-1 bg-muted rounded w-2/3" />
                        <div className="h-1 bg-muted rounded" />
                      </div>
                      <div className="border-l border-dashed border-border" />
                      <div className="flex-1 space-y-1">
                        <div className="h-1.5 rounded" style={{ backgroundColor: primaryColor }} />
                        <div className="h-1 bg-muted rounded" />
                        <div className="h-1 bg-muted rounded w-2/3" />
                        <div className="h-1 bg-muted rounded" />
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">{tpl.description}</p>
              </button>
            );
          })}
        </div>

        {/* Filas mínimas */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="minRows" className="text-sm font-medium mb-2 block">
              Filas mínimas en las tablas
            </Label>
            <Input
              id="minRows"
              type="number"
              min={1}
              max={20}
              value={prescriptionMinRows}
              onChange={(e) => setPrescriptionMinRows(Number(e.target.value))}
              className="w-28"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Si recetas menos medicamentos, las filas sobrantes se imprimen vacías para llenarlas a mano.
              Si recetas más, la tabla crece automáticamente.
            </p>
          </div>

          <div>
            <Label htmlFor="rneNumber" className="text-sm font-medium mb-2 block">
              Registro Nacional de Especialista (RNE)
            </Label>
            <Input
              id="rneNumber"
              value={rneNumber}
              onChange={(e) => setRneNumber(e.target.value)}
              placeholder="Ej: 51692"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Se muestra junto a tu colegiatura en el encabezado de la receta
            </p>
          </div>
        </div>

        {/* Toggles de bloques */}
        <div className="mt-6">
          <p className="text-sm font-medium mb-3">Bloques que se imprimen</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {OPTION_LABELS.map((option) => (
              <label
                key={option.key}
                className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/30 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={!!prescriptionOptions[option.key]}
                  onChange={() => toggleOption(option.key)}
                  className="mt-0.5 w-4 h-4 cursor-pointer"
                  style={{ accentColor: primaryColor }}
                />
                <span>
                  <span className="text-sm font-medium block">{option.label}</span>
                  <span className="text-xs text-muted-foreground">{option.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </Card>

      {/* Logo de Empresa */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <Image className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Logo de tu Empresa
            </h2>
            <p className="text-sm text-muted-foreground">
              Sube tu logo para que aparezca en las recetas médicas
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo Rectangular */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Logo Rectangular (Encabezado de receta)
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Recomendado: 400x120px, formato PNG o JPG, máximo 1.5MB
            </p>
            <input
              ref={rectInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(e) => handleFileUpload(e, 'rectangular')}
            />
            {logoRectangular ? (
              <div className="relative border-2 border-dashed border-border rounded-lg p-4 bg-muted/30">
                <img
                  src={logoRectangular}
                  alt="Logo rectangular"
                  className="max-h-24 max-w-full mx-auto object-contain"
                />
                <div className="flex gap-2 justify-center mt-3">
                  <Button size="sm" variant="outline" onClick={() => rectInputRef.current?.click()}>
                    <Upload className="w-3 h-3 mr-1" />
                    Cambiar
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => setLogoRectangular(null)}>
                    <Trash2 className="w-3 h-3 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-muted/30 transition-colors"
                onClick={() => rectInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium text-muted-foreground">
                  Carga el logo de tu empresa
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Haz clic para seleccionar un archivo
                </p>
              </div>
            )}
          </div>

          {/* Logo Cuadrado */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Logo Cuadrado (Pie de receta y sidebar)
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Recomendado: 200x200px, formato PNG o JPG, máximo 1.5MB
            </p>
            <input
              ref={squareInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(e) => handleFileUpload(e, 'square')}
            />
            {logoSquare ? (
              <div className="relative border-2 border-dashed border-border rounded-lg p-4 bg-muted/30">
                <img
                  src={logoSquare}
                  alt="Logo cuadrado"
                  className="w-24 h-24 mx-auto object-contain"
                />
                <div className="flex gap-2 justify-center mt-3">
                  <Button size="sm" variant="outline" onClick={() => squareInputRef.current?.click()}>
                    <Upload className="w-3 h-3 mr-1" />
                    Cambiar
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => setLogoSquare(null)}>
                    <Trash2 className="w-3 h-3 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-muted/30 transition-colors"
                onClick={() => squareInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium text-muted-foreground">
                  Carga tu logo cuadrado
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Haz clic para seleccionar un archivo
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Colores de la Página */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
            <Palette className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Colores Personalizados
            </h2>
            <p className="text-sm text-muted-foreground">
              Elige los colores que se aplicarán a tu receta médica
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
          <div>
            <Label className="text-sm font-medium mb-2 block">Color Primario</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-border"
              />
              <Input
                value={primaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(val)) setPrimaryColor(val);
                }}
                className="font-mono w-28"
                maxLength={7}
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Color Secundario</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-border"
              />
              <Input
                value={secondaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(val)) setSecondaryColor(val);
                }}
                className="font-mono w-28"
                maxLength={7}
              />
            </div>
          </div>

          <div>
            <Button variant="outline" onClick={handleResetColors}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Restaurar Predeterminados
            </Button>
          </div>
        </div>

        {/* Preview de colores */}
        <div className="mt-6 p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground mb-3">Vista previa de colores:</p>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded" style={{ backgroundColor: primaryColor }}></div>
              <span className="text-sm">Primario</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded" style={{ backgroundColor: secondaryColor }}></div>
              <span className="text-sm">Secundario</span>
            </div>
            <div className="ml-4 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: primaryColor }}>
              Botón de ejemplo
            </div>
            <div className="px-4 py-2 rounded-lg text-sm font-medium border-2" style={{ borderColor: primaryColor, color: primaryColor }}>
              Borde de ejemplo
            </div>
          </div>
        </div>
      </Card>

      {/* Datos Profesionales */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Datos Profesionales para Receta
            </h2>
            <p className="text-sm text-muted-foreground">
              Información adicional que aparecerá en tus recetas médicas
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="businessName" className="text-sm font-medium mb-2 block">
              <Building2 className="w-4 h-4 inline mr-1" />
              Nombre del Consultorio / Empresa (opcional)
            </Label>
            <Input
              id="businessName"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Ej: Consultorio Médico Dr. Pérez"
              maxLength={255}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Se mostrará en el encabezado de la receta si no tienes logo
            </p>
          </div>

          <div>
            <Label htmlFor="emailProfessional" className="text-sm font-medium mb-2 block">
              <Mail className="w-4 h-4 inline mr-1" />
              Email Profesional
            </Label>
            <Input
              id="emailProfessional"
              type="email"
              value={emailProfessional}
              onChange={(e) => setEmailProfessional(e.target.value)}
              placeholder="Ej: doctor@miconsultorio.com"
              maxLength={255}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Se mostrará en el pie de la receta médica
            </p>
          </div>

          <div>
            <Label htmlFor="phoneProfessional" className="text-sm font-medium mb-2 block">
              <Phone className="w-4 h-4 inline mr-1" />
              Teléfono de contacto
            </Label>
            <Input
              id="phoneProfessional"
              value={phoneProfessional}
              onChange={(e) => setPhoneProfessional(e.target.value)}
              placeholder="Ej: +941 628 379 - WhatsApp"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Aparece en el pie de la receta si activaste "Pie de contacto"
            </p>
          </div>

          <div>
            <Label htmlFor="addressProfessional" className="text-sm font-medium mb-2 block">
              <MapPin className="w-4 h-4 inline mr-1" />
              Dirección del consultorio
            </Label>
            <Input
              id="addressProfessional"
              value={addressProfessional}
              onChange={(e) => setAddressProfessional(e.target.value)}
              placeholder="Ej: Av. del Ejército 1509, Iquitos"
              maxLength={255}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Aparece en el pie de la receta si activaste "Pie de contacto"
            </p>
          </div>
        </div>

        {/* Info del doctor (solo lectura) */}
        <div className="mt-6 p-4 bg-muted/30 rounded-lg border">
          <p className="text-sm font-medium mb-3">Datos que aparecerán automáticamente en la receta:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Nombre:</span>
              <p className="font-medium">Dr(a). {doctorData?.full_name || user?.full_name}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Especialidad:</span>
              <p className="font-medium">{doctorData?.specialization || 'No especificada'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Colegiatura:</span>
              <p className="font-medium">{doctorData?.license_number || 'No especificada'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Botón guardar final */}
      <div className="flex justify-end gap-3 pb-6">
        <Button variant="outline" onClick={openPreview}>
          <Eye className="w-4 h-4 mr-2" />
          Vista Previa Receta
        </Button>
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      {/* Firma, hoja membretada, Gmail y guías nutricionales: cada tarjeta guarda por su cuenta */}
      <DoctorDocumentsSettings doctorData={doctorData} />
    </div>
  );
}
